/**
 * @module useWarehouse
 * Shared singleton state for warehouse / store inventory management.
 *
 * All state lives at module scope so the inventory, stock-in, stock-out,
 * and par-stock views share data without re-fetching.
 * Uses accountingApi (same backend as sales and petty cash, port 3000).
 */

import { ref, computed, watch } from "vue";
import { accountingApi } from "@/utils/api.js";
import { toLocalYM } from "@/utils/dateLocal.js";

const API = "/api/accounting/warehouse";

// ── Core data ─────────────────────────────────────────────────────────────────

const categories = ref([]); // warehouse_item_categories
const items = ref([]);       // pack variants (warehouse_items) — for item management dialog
const baseItems = ref([]);   // base items (items table) — for transaction/ledger item picker
const transactions = ref([]);
const inventory = ref([]);
const parLevels = ref([]);
const currentPeriodStatus = ref(null);

// Options: locations, brands, suppliers, last stock-in prices (from warehouse/options endpoint)
const locations = ref([]);
const brands = ref([]);
const suppliers = ref([]);
const lastStockInPrices = ref({}); // { itemId: unitPrice }

// Active view controlled by route name
const activeView = ref("inventory");

// Selected month for transactions view (YYYY-MM)
const selectedMonth = ref(toLocalYM());

// The Period filter on the Stock Journal and the Stock Ledger (2026-10-20): the app-bar
// month is the default; "all" shows the whole history; "range" a From / To of the page's own.
// Not remembered between visits — a forgotten range would make the pages look empty.
const periodMode = ref("month"); // 'month' | 'all' | 'range'
const periodFrom = ref(null);
const periodTo = ref(null);

/** The query params the current period asks the server for. */
function periodParams() {
  if (periodMode.value === "all") return {};
  if (periodMode.value === "range") {
    const p = {};
    if (periodFrom.value) p.from = periodFrom.value;
    if (periodTo.value) p.to = periodTo.value;
    return p;
  }
  return { month: selectedMonth.value };
}

/** The period in words, for chips and PDFs. */
function periodLabel() {
  if (periodMode.value === "all") return "All history";
  if (periodMode.value === "range") {
    if (!periodFrom.value && !periodTo.value) return "All history";
    return `${periodFrom.value || "start"} to ${periodTo.value || "today"}`;
  }
  return selectedMonth.value;
}

// Month summary (stock in/out totals for selected month)
const monthSummary = ref({ stockInQty: 0, stockInValue: 0, stockOutQty: 0, stockOutValue: 0 });

// Ledger state (per-item transaction history)
const ledgerItemId = ref(null);
const ledgerLines = ref([]);
const ledgerOpeningQty = ref(0);
const ledgerOpeningValue = ref(0);

// Loading and error state
const loading = ref(false);
const error = ref(null);
// Set true when the last stock-journal fetch failed, so the view can show a
// clear error + Retry affordance instead of a silently-empty table.
const transactionsError = ref(false);

// Snackbar
const snackbar = ref(false);
const snackbarText = ref("");
const snackbarColor = ref("success");

// ── Dialog state ──────────────────────────────────────────────────────────────

// Transaction dialog (create/edit/duplicate)
const transactionDialogOpen = ref(false);
const transactionDialogMode = ref("create"); // create | edit | duplicate
const selectedTransaction = ref(null);

// Item dialog (manage items)
const itemDialogOpen = ref(false);

// Category dialog (manage categories)
const categoryDialogOpen = ref(false);

// Filter dialog
const filterDialogOpen = ref(false);

// Transaction type filter
const filterType = ref(null);
// Items filter: one item or several (2026-10-30; was a single filterItemId)
const filterItemIds = ref([]);
// The Stock Journal's filter window (phones, and the Warehouse ⋮ "Filter")
const inventoryFilterOpen = ref(false);
const filterLocationId = ref(null);
const filterSupplierId = ref(null);
const filterBrandId = ref(null);
// Stock Journal (2026-10-19): where the stock came FROM / went TO — a place, or a sub cost-centre
const filterFromLocationId = ref(null);
const filterToLocationId = ref(null);
const filterCategoryId = ref(null);

const WAREHOUSE_FILTERS_KEY = "warehouseFilters";

// Restore filter state from localStorage on init
const savedWarehouseFilters = localStorage.getItem(WAREHOUSE_FILTERS_KEY);
if (savedWarehouseFilters) {
  try {
    const parsed = JSON.parse(savedWarehouseFilters);
    filterType.value = parsed.filterType ?? null;
    filterItemIds.value = Array.isArray(parsed.filterItemIds) ? parsed.filterItemIds : parsed.filterItemId != null ? [parsed.filterItemId] : [];
    filterLocationId.value = parsed.filterLocationId ?? null;
    filterSupplierId.value = parsed.filterSupplierId ?? null;
    filterBrandId.value = parsed.filterBrandId ?? null;
    filterFromLocationId.value = parsed.filterFromLocationId ?? null;
    filterToLocationId.value = parsed.filterToLocationId ?? null;
    // filterCategoryId now holds an item_type code ('INGR','PACK',...), not a category integer.
    // Discard stale integer values left over from the pre-refactor model.
    const VALID_TYPES = ['INGR','PACK','BEVE','CLEA','STFO','OTHE'];
    const saved = parsed.filterCategoryId ?? null;
    filterCategoryId.value = VALID_TYPES.includes(saved) ? saved : null;
    selectedMonth.value = parsed.selectedMonth ?? toLocalYM();
  } catch {}
}

// Auto-persist filter state to localStorage on change
watch([filterType, filterItemIds, filterLocationId, filterSupplierId, filterBrandId, filterFromLocationId, filterToLocationId, filterCategoryId, selectedMonth], () => {
  localStorage.setItem(
    WAREHOUSE_FILTERS_KEY,
    JSON.stringify({
      filterType: filterType.value,
      filterItemIds: filterItemIds.value,
      filterLocationId: filterLocationId.value,
      filterSupplierId: filterSupplierId.value,
      filterBrandId: filterBrandId.value,
      filterFromLocationId: filterFromLocationId.value,
      filterToLocationId: filterToLocationId.value,
      filterCategoryId: filterCategoryId.value,
      selectedMonth: selectedMonth.value,
    }),
  );
}, { deep: true });

// ── Snackbar helper ───────────────────────────────────────────────────────────

/** Show a snackbar notification. */
function showSnackbar(text, color = "success") {
  snackbarText.value = text;
  snackbarColor.value = color;
  snackbar.value = true;
}

// ── Computed ──────────────────────────────────────────────────────────────────

/** Total inventory value across all items. */
const totalInventoryValue = computed(() =>
  inventory.value.reduce((sum, item) => sum + (parseFloat(item.total_value) || 0), 0),
);

/** Number of active filter criteria. */
const activeFilterCount = computed(() => {
  let count = 0;
  if (filterType.value) count++;
  if (filterItemIds.value.length) count++;
  if (filterLocationId.value) count++;
  if (filterSupplierId.value) count++;
  if (filterBrandId.value) count++;
  if (filterFromLocationId.value) count++;
  if (filterToLocationId.value) count++;
  if (filterCategoryId.value) count++;
  return count;
});

// ── API: Categories ────────────────────────────────────────────────────────────

/** Fetch all item categories (?include_inactive for management). */
async function fetchCategories(includeInactive = false) {
  try {
    const params = includeInactive ? { include_inactive: 'true' } : {};
    const { data } = await accountingApi.get(`${API}/categories`, { params });
    categories.value = data;
  } catch (err) {
    console.error("Failed to fetch categories:", err);
    showSnackbar("Failed to load categories", "error");
  }
}

/** Create a new item category. */
async function createCategoryApi(catData) {
  const { data } = await accountingApi.post(`${API}/categories`, catData);
  await fetchCategories(true);
  showSnackbar(`Category "${data.name}" created`);
  return data;
}

/** Update an item category. */
async function updateCategoryApi(id, catData) {
  const { data } = await accountingApi.put(`${API}/categories/${id}`, catData);
  await fetchCategories(true);
  // Refresh items so their category_color/name is up to date
  await fetchItems(true);
  showSnackbar(`Category "${data.name}" updated`);
  return data;
}

/** Delete an item category (fails if items are assigned). */
async function deleteCategoryApi(id) {
  await accountingApi.delete(`${API}/categories/${id}`);
  await fetchCategories(true);
  showSnackbar("Category deleted");
}

// ── API: Items ────────────────────────────────────────────────────────────────

/** Fetch all warehouse pack variants (warehouse_items). */
async function fetchItems(includeInactive = false) {
  try {
    const params = includeInactive ? { include_inactive: 'true' } : {};
    const { data } = await accountingApi.get(`${API}/items`, { params });
    items.value = data;
  } catch (err) {
    console.error("Failed to fetch items:", err);
    showSnackbar("Failed to load items", "error");
  }
}

/** Fetch base items (items table) that have warehouse pack variants.
 *  Used by the transaction dialog item picker — transactions reference items.id. */
async function fetchBaseItems() {
  try {
    const { data } = await accountingApi.get(`${API}/base-items`);
    baseItems.value = data;
    // Self-heal stale item ids left in localStorage (e.g. from before the N:1 refactor,
    // when they held warehouse_items ids): drop any that is not a real base item, so it
    // stops hiding rows.
    const known = filterItemIds.value.filter((id) => data.some((i) => i.id === id));
    if (known.length !== filterItemIds.value.length) filterItemIds.value = known;
  } catch (err) {
    console.error("Failed to fetch base items:", err);
  }
}

/** Create a new warehouse item. */
async function createItemApi(itemData) {
  const { data } = await accountingApi.post(`${API}/items`, itemData);
  await fetchItems(true); // refresh list including inactive
  showSnackbar(`Item "${data.name}" created`);
  return data;
}

/** Update a warehouse item. */
async function updateItemApi(id, itemData) {
  const { data } = await accountingApi.put(`${API}/items/${id}`, itemData);
  await fetchItems(true);
  showSnackbar(`Item "${data.name}" updated`);
  return data;
}

/** Delete a warehouse item (fails if transactions exist). */
async function deleteItemApi(id) {
  await accountingApi.delete(`${API}/items/${id}`);
  await fetchItems(true);
  showSnackbar("Item deleted");
}

// ── API: Transactions ─────────────────────────────────────────────────────────

/** Fetch transactions for the selected month with active filters. */
async function fetchTransactions() {
  loading.value = true;
  transactionsError.value = false;
  try {
    // NOTE: filterType is applied in the browser, not here. Asking the server
    // for a single type meant the journal's tab counters — computed from
    // whatever came back — all read 0 as soon as any tab was selected, so a
    // month with plenty of entries looked completely empty (Iqbal, 2026-09-05).
    const params = { ...periodParams() };
    if (filterItemIds.value.length) params.item_id = filterItemIds.value.join(",");
    if (filterLocationId.value) params.location_id = filterLocationId.value;
    if (filterSupplierId.value) params.supplier_id = filterSupplierId.value;
    if (filterBrandId.value) params.brand_id = filterBrandId.value;
    if (filterFromLocationId.value) params.from_location_id = filterFromLocationId.value;
    if (filterToLocationId.value) params.to_location_id = filterToLocationId.value;

    const { data } = await accountingApi.get(`${API}/transactions`, { params });
    transactions.value = data;
  } catch (err) {
    console.error("Failed to fetch transactions:", err);
    // Flag the failure so the journal shows an error + Retry instead of an
    // empty table that reads as "0 entries" and only recovers on a manual reload.
    transactionsError.value = true;
    showSnackbar("Failed to load transactions", "error");
  } finally {
    loading.value = false;
  }
}

// Auto-refetch transactions when any filter changes, but only while the user is
// actively on the journal view. This avoids triggering a fetch when the user is
// on inventory, ledger, or par-stock — those views manage their own data loading.
// filterType is deliberately NOT here: it is applied in the browser now, so
// switching tab is instant and needs no round trip.
watch([filterItemIds, filterLocationId, filterSupplierId, filterBrandId, filterFromLocationId, filterToLocationId], () => {
  if (activeView.value === "journal") {
    fetchTransactions();
  }
});

/** Create a stock movement. */
async function createTransactionApi(txnData) {
  const { data } = await accountingApi.post(`${API}/transactions`, txnData);
  // Refresh both transactions and inventory after a stock movement
  await Promise.all([fetchTransactions(), fetchInventory(), fetchMonthSummary()]);
  showSnackbar("Transaction created");
  return data;
}

/** Update a stock movement. */
async function updateTransactionApi(id, txnData) {
  const { data } = await accountingApi.put(`${API}/transactions/${id}`, txnData);
  await Promise.all([fetchTransactions(), fetchInventory(), fetchMonthSummary()]);
  showSnackbar("Transaction updated");
  return data;
}

/** Delete a stock movement. */
async function deleteTransactionApi(id) {
  await accountingApi.delete(`${API}/transactions/${id}`);
  await Promise.all([fetchTransactions(), fetchInventory(), fetchMonthSummary()]);
  showSnackbar("Transaction deleted");
}

/**
 * Submit a physical stock count — one 'adjustment' transaction is recorded
 * per item whose counted quantity differs from the system's balance; an
 * exact match is skipped server-side. See submitStockCount() in
 * database/warehouse.js for the full reconciliation-workflow rationale.
 * @param {{date: string, locationId?: number, brandId?: number, notes?: string,
 *   counts: Array<{itemId: number, countedQty: number}>}} payload
 */
async function submitStockCountApi(payload) {
  const { data } = await accountingApi.post(`${API}/inventory/count`, payload);
  await Promise.all([fetchTransactions(), fetchInventory(), fetchMonthSummary()]);
  showSnackbar(data.message || "Stock count submitted");
  return data;
}

/**
 * Record wastage (spoilage, drops, expiry) — one negative 'adjustment'
 * transaction per item, no "counted vs system" comparison. See
 * recordWastage() in database/warehouse.js.
 * @param {{date: string, locationId?: number, brandId?: number, notes?: string,
 *   items: Array<{itemId: number, quantity: number, notes?: string}>}} payload
 */
async function recordWastageApi(payload) {
  const { data } = await accountingApi.post(`${API}/wastage`, payload);
  await Promise.all([fetchTransactions(), fetchInventory(), fetchMonthSummary()]);
  showSnackbar(data.message || "Wastage recorded");
  return data;
}

/**
 * Batch Stock Out: one destination (branch + brand), many items, dated once
 * (Maurizio, 2026-09-11, at Iqbal's request — same shape as recordWastageApi
 * above). Server-side write pattern in recordStockOutBatch() (database/warehouse.js).
 * @param {{date: string, toLocationId: number, toBrandId?: number, notes?: string,
 *   items: Array<{itemId: number, quantity: number, notes?: string}>}} payload
 */
async function recordStockOutBatchApi(payload) {
  const { data } = await accountingApi.post(`${API}/stock-out`, payload);
  await Promise.all([fetchTransactions(), fetchInventory(), fetchMonthSummary()]);
  showSnackbar(data.message || "Stock out recorded");
  return data;
}

/**
 * Record an inter-brand/inter-branch transfer — two linked 'adjustment'
 * transactions (sender/receiver). NOT posted through "Post consumption";
 * see recordTransfer() in database/warehouse.js and postTransfers() in
 * transfer-posting.js for the dedicated GL posting.
 * @param {{date: string, itemId: number, quantity: number, fromLocationId?: number,
 *   fromBrandId?: number, fromStaffFood?: boolean, toLocationId?: number,
 *   toBrandId?: number, toStaffFood?: boolean, notes?: string}} payload
 */
async function recordTransferApi(payload) {
  const { data } = await accountingApi.post(`${API}/transfers`, payload);
  await Promise.all([fetchTransactions(), fetchInventory(), fetchMonthSummary()]);
  showSnackbar(data.message || "Transfer recorded");
  return data;
}

// ── API: Inventory ────────────────────────────────────────────────────────────

/** Fetch current stock on hand for all active items. */
async function fetchInventory() {
  try {
    const { data } = await accountingApi.get(`${API}/inventory`);
    inventory.value = data;
  } catch (err) {
    console.error("Failed to fetch inventory:", err);
    showSnackbar("Failed to load inventory", "error");
  }
}

/** Fetch monthly summary (stock in/out totals). */
async function fetchMonthSummary() {
  try {
    const { data } = await accountingApi.get(`${API}/inventory/summary`, {
      params: { month: selectedMonth.value },
    });
    monthSummary.value = data;
  } catch (err) {
    console.error("Failed to fetch month summary:", err);
  }
}

// ── API: Ledger ──────────────────────────────────────────────────────────

/** Fetch per-item transaction history for the stock ledger view. */
async function fetchLedger() {
  if (!ledgerItemId.value) {
    ledgerLines.value = [];
    ledgerOpeningQty.value = 0;
    ledgerOpeningValue.value = 0;
    return;
  }
  loading.value = true;
  try {
    const params = { item_id: ledgerItemId.value, ...periodParams() };
    const { data } = await accountingApi.get(`${API}/ledger`, { params });
    // Backend returns { openingQty, openingValue, rows }
    ledgerLines.value = data.rows;
    ledgerOpeningQty.value = data.openingQty || 0;
    ledgerOpeningValue.value = data.openingValue || 0;
  } catch (err) {
    console.error("Failed to fetch ledger:", err);
    showSnackbar("Failed to load ledger", "error");
  } finally {
    loading.value = false;
  }
}

// ── API: Periods ──────────────────────────────────────────────────────────────

/** Fetch all warehouse periods. */

/** Fetch status of the selected month's period. */
async function fetchPeriodStatus() {
  try {
    const { data } = await accountingApi.get(`${API}/periods/${selectedMonth.value}`);
    currentPeriodStatus.value = data;
  } catch (err) {
    console.error("Failed to fetch period status:", err);
    currentPeriodStatus.value = null;
  }
}

/** Close the selected month's period. */
async function closePeriod() {
  await accountingApi.post(`${API}/periods/${selectedMonth.value}/close`);
  await fetchPeriodStatus();
  showSnackbar(`Period ${selectedMonth.value} closed`);
}

/** Reopen the selected month's period (appadmin only). */
async function reopenPeriod() {
  await accountingApi.post(`${API}/periods/${selectedMonth.value}/reopen`);
  await fetchPeriodStatus();
  showSnackbar(`Period ${selectedMonth.value} reopened`);
}

// ── API: Par Stock ────────────────────────────────────────────────────────────

/** Fetch par levels, optionally for a specific location. */
async function fetchParLevels(locationId = null) {
  try {
    const params = locationId ? { location_id: locationId } : {};
    const { data } = await accountingApi.get(`${API}/par-levels`, { params });
    parLevels.value = data;
  } catch (err) {
    console.error("Failed to fetch par levels:", err);
    showSnackbar("Failed to load par levels", "error");
  }
}

/** Set a par level (upsert). */
async function setParLevelApi(itemId, locationId, parQty) {
  await accountingApi.put(`${API}/par-levels`, { itemId, locationId, parQty });
  await fetchParLevels();
  showSnackbar("Par level updated");
}

/** Delete a par level. */
async function deleteParLevelApi(id) {
  await accountingApi.delete(`${API}/par-levels/${id}`);
  await fetchParLevels();
  showSnackbar("Par level deleted");
}

// ── API: Options ──────────────────────────────────────────────────────────────

/** Fetch locations, brands, and suppliers for form dropdowns. */
async function fetchOptions() {
  try {
    const { data } = await accountingApi.get(`${API}/options`);
    locations.value = data.locations || [];
    brands.value = data.brands || [];
    suppliers.value = data.suppliers || [];
    lastStockInPrices.value = data.lastStockInPrices || {};
  } catch (err) {
    console.error("Failed to fetch warehouse options:", err);
  }
}

/** Fetch locations (including "Warehouse"), brands, and suppliers for the delivery form. */
async function fetchDeliveryOptions() {
  try {
    const { data } = await accountingApi.get(`${API}/delivery-options`);
    locations.value = data.locations || [];
    brands.value = data.brands || [];
    suppliers.value = data.suppliers || [];
  } catch (err) {
    console.error("Failed to fetch delivery options:", err);
  }
}

// ── Dialog helpers ────────────────────────────────────────────────────────────

/** Open the transaction dialog in create mode. */
function openCreateDialog(type = null) {
  transactionDialogMode.value = "create";
  selectedTransaction.value = type ? { transactionType: type } : null;
  transactionDialogOpen.value = true;
}

/** Open the transaction dialog in edit mode. */
function openEditDialog(txn) {
  transactionDialogMode.value = "edit";
  selectedTransaction.value = txn;
  transactionDialogOpen.value = true;
}

/** Open the transaction dialog in duplicate mode. */
function openDuplicateDialog(txn) {
  transactionDialogMode.value = "duplicate";
  selectedTransaction.value = txn;
  transactionDialogOpen.value = true;
}

/** Clear all transaction filters. */
function clearFilters() {
  filterType.value = null;
  filterItemIds.value = [];
  filterLocationId.value = null;
  filterSupplierId.value = null;
  filterBrandId.value = null;
  filterFromLocationId.value = null;
  filterToLocationId.value = null;
  filterCategoryId.value = null;
}

// Changing the Period filter reloads whichever of the two pages is open
watch([periodMode, periodFrom, periodTo], () => {
  if (periodMode.value === "range" && periodFrom.value && periodTo.value && periodFrom.value > periodTo.value) return;
  if (activeView.value === "journal") fetchTransactions();
  else if (activeView.value === "ledger" && ledgerItemId.value) fetchLedger();
});

/** Refresh all data views when the selected month changes. */
async function onMonthChange() {
  const fetches = [fetchPeriodStatus(), fetchMonthSummary()];
  if (activeView.value === "journal") {
    fetches.push(fetchTransactions());
  } else if (activeView.value === "ledger" && ledgerItemId.value) {
    fetches.push(fetchLedger());
  } else if (activeView.value === "monthly-summary") {
    // No shared fetch here — WarehouseMonthlySummaryView.vue watches
    // selectedMonth itself and re-fetches its own report endpoint.
  } else {
    fetches.push(fetchTransactions());
  }
  await Promise.all(fetches);
}

// ── Export ─────────────────────────────────────────────────────────────────────

/**
 * Singleton composable for warehouse state. All consumers share the same reactive
 * refs — calling this multiple times returns the same object. Safe to use in
 * multiple components; each gets a consistent view of inventory, transactions, etc.
 *
 *   loading, error, snackbar, snackbarText, snackbarColor, activeView, filterType,
 *   filterItemIds, filterLocationId, filterSupplierId, filterBrandId, ... }}
 */
export function useWarehouse() {
  return {
    // Data
    categories,
    items,
    baseItems,
    transactions,
    inventory,
    parLevels,
    currentPeriodStatus,
    locations,
    brands,
    suppliers,
    lastStockInPrices,
    activeView,
    selectedMonth,
    periodMode, periodFrom, periodTo, periodLabel,
    monthSummary,
    loading,
    error,
    transactionsError,

    // Snackbar
    snackbar,
    snackbarText,
    snackbarColor,
    showSnackbar,

    // Dialog state
    transactionDialogOpen,
    transactionDialogMode,
    selectedTransaction,
    itemDialogOpen,
    categoryDialogOpen,
    filterDialogOpen,

    // Filters
    filterType,
    filterItemIds,
    inventoryFilterOpen,
    filterLocationId,
    filterSupplierId,
    filterBrandId,
    filterFromLocationId,
    filterToLocationId,
    filterCategoryId,
    activeFilterCount,

    // Computed
    totalInventoryValue,

    // API: Categories
    fetchCategories,
    createCategoryApi,
    updateCategoryApi,
    deleteCategoryApi,

    // API: Items
    fetchItems,
    fetchBaseItems,
    createItemApi,
    updateItemApi,
    deleteItemApi,

    // API: Transactions
    fetchTransactions,
    createTransactionApi,
    updateTransactionApi,
    deleteTransactionApi,

    // API: Inventory
    fetchInventory,
    fetchMonthSummary,
    submitStockCountApi,
    recordWastageApi,
    recordStockOutBatchApi,
    recordTransferApi,

    // API: Ledger
    ledgerItemId,
    ledgerLines,
    ledgerOpeningQty,
    ledgerOpeningValue,
    fetchLedger,

    // API: Periods
    fetchPeriodStatus,
    closePeriod,
    reopenPeriod,
    onMonthChange,

    // API: Par stock
    fetchParLevels,
    setParLevelApi,
    deleteParLevelApi,

    // API: Options
    fetchOptions,
    fetchDeliveryOptions,

    // Dialog helpers
    openCreateDialog,
    openEditDialog,
    openDuplicateDialog,
    clearFilters,
  };
}
